import { Box, Center, Container, Flex, Spinner } from '@chakra-ui/react'
import SystemFinderProgressSteps from '../../pages/SystemFinder/ProgressStep/SystemFinderProgressSteps'
import { Breadcrumbs, Seo } from '@stocker/ui-components/design-system'
import { decryptBase64, encryptBase64, useStep } from '@stocker/ui-components/helpers'
import { useRouter } from 'next/router'
import { useProductCategoryListingSystemFinderQuery, useSystemFinderListingQuery } from '@stocker/codegen/pimcore'
import { useSystemsQuery } from '@stocker/codegen/configurator-backend'
import { useEffect, useReducer, useState } from 'react'
import { useIntl } from 'react-intl'
import { SystemFinderSelectionPage } from './steps/SelectionPage'
import { SystemFinderStartPage } from './steps/StartPage'
import { SystemFinderSystemSelectPage } from './steps/SystemSelectPage'
import { linkableConfiguratorSystem } from './configuratorLink'

export const SystemFinder = () => {
  const { formatMessage } = useIntl()

  const [currentStep, { setStep }] = useStep({ maxStep: 4, initialStep: 0 })
  const [systemFinderStarted, setSystemFinderStarted] = useState(false)

  const { data: systemFinderOptions, isLoading: isLoadingSystemFinderOptions } = useSystemFinderListingQuery()
  const systemFinderListing = systemFinderOptions?.getSystemFinderListing?.edges?.[0]?.node ?? null

  const [stepsValues, dispatch] = useReducer(stepReducer, [{ step: 0, name: '', id: '' }])
  function stepReducer (state: Array<{ step: number, name: string, id: string }>, action: { step: number, name: string, id: string }) {
    // When setting a step value, remove all subsequent steps (user changed selection, need to re-select following steps)
    const filteredState = state.filter(s => s.step < action.step)

    return [
      ...filteredState,
      {
        step: action.step,
        name: action.name,
        id: action.id,
      }]
  }

  const [urlValues, setUrlValues] = useState<string[]>()

  // on reload set the urlValues into stepValues
  useEffect(() => {
    urlValues?.map((value, index) => { dispatch({ step: index, name: '', id: value }) })
  }, [urlValues])

  const router = useRouter()
  // here we put the configuration values in the url as base64
  useEffect(() => {
    if (!systemFinderStarted) return
    router.push(`?values=${encryptBase64(stepsValues.map(e => e.id))}`)
  }, [JSON.stringify(stepsValues)])


  // here we decrypt the url param again
  useEffect(() => {
    if (router.query.values) {
      const decryptedUrlValues = decryptBase64(String(router.query.values))
      if (decryptedUrlValues) {
        // only setStep if systemFinder has not started yet so it only sets steps on page reload not anytime during configuration
        if (!systemFinderStarted) {
          setStep(decryptedUrlValues.length)
        }
        setSystemFinderStarted(true)
      }
      setUrlValues(decryptedUrlValues)
    }
  }, [router.query.values])

  const firstStepValue = urlValues?.length !== 0 ? { step: 0, name: systemFinderListing?.project?.find(e => e?.projectFilter?.id === urlValues?.[0])?.headline, id: urlValues?.[0] } : stepsValues.find(e => e.step === 0)
  const secondStepValue = urlValues?.length !== 0 ? { step: 1, name: systemFinderListing?.operatingMode?.find(e => e?.operatingModeFilter?.id === urlValues?.[1])?.headline, id: urlValues?.[1] } : stepsValues.find(e => e.step === 1)
  const thirdStepValue = urlValues?.length !== 0 ? { step: 2, name: systemFinderListing?.pressure?.find(e => e?.pressureFilter?.id === urlValues?.[2])?.headline, id: urlValues?.[2] } : stepsValues.find(e => e.step === 2)
  const fourthStepValue = urlValues?.length !== 0 ? { step: 3, name: systemFinderListing?.execution?.find(e => e?.executionFilter?.id === urlValues?.[3])?.headline, id: urlValues?.[3] } : stepsValues.find(e => e.step === 3)

  // Load configurator systems from backend to get UUID mappings
  const { data: systems } = useSystemsQuery()

  // first filters like isSystem - directly on pimcore data object ProductCategory
  const { data: systemFinderSystems } = useProductCategoryListingSystemFinderQuery({ filter: '{"isSystem": "1"}' })
  const systemFinderSystemsData = systemFinderSystems?.getProductCategoryListing?.edges

  const selectedValuesIds = stepsValues.map(e => e.id)

  // here we get all possible system with the selected values
  // Use selectedValuesIds as primary source, only fall back to urlValues if selectedValuesIds is empty
  const valuesToCheck = selectedValuesIds.filter(id => id !== '').length > 0
    ? selectedValuesIds.filter(id => id !== '')
    : (urlValues ?? [])

  // Check if state is in sync - if not, don't filter yet (return all systems)
  const stateInSync = valuesToCheck.length >= currentStep || currentStep === 0

  const allPossibleSystems = systemFinderSystemsData?.filter(system => {
    const systemFilters = system?.node?.systemFinderFilters ?? []
    const allMatchingValues = systemFilters.filter(filter => valuesToCheck.includes(filter?.id ?? ''))

    // Results page (step 4): show only systems that contain ALL user's selected values
    // (system may have MORE filters than user selected, that's OK)
    if (currentStep >= 4) {
      return allMatchingValues.length === valuesToCheck.length && valuesToCheck.length > 0
    }

    // Intermediate steps: show systems that could still be valid
    return allMatchingValues.length >= currentStep
  })

  // Determine which pressure options to use based on first step selection
  const firstStepSelectedName = firstStepValue?.name ?? ''
  const usePressureStep = firstStepSelectedName === 'Sanierung'

  // Determine selected value for each step - show "Übersprungen" only for steps that were skipped (no selection made)
  const getStepSelectedValue = (stepIndex: number, stepValue: { name?: string | null } | undefined) => {
    // If step has a selected value, always show it
    if (stepValue?.name) return stepValue.name
    // If we've passed this step but it has no value, it was skipped
    if (currentStep > stepIndex) return 'Übersprungen'
    return ''
  }

  const steps = [
    { title: formatMessage({ id: 'system-finder--builden-project' }), selectedValue: getStepSelectedValue(0, firstStepValue) },
    { title: formatMessage({ id: 'system-finder--operating-mode' }), selectedValue: getStepSelectedValue(1, secondStepValue) },
    { title: formatMessage({ id: 'system-finder--pressure' }), selectedValue: getStepSelectedValue(2, thirdStepValue) },
    { title: formatMessage({ id: 'system-finder--version' }), selectedValue: getStepSelectedValue(3, fourthStepValue) },
  ]

  const pressureStep = systemFinderListing?.pressure?.map(pressure => ({
    id: pressure?.pressureFilter?.id ?? '',
    heading: { text: pressure?.headline ?? '' },
    image: { fullpath: pressure?.image?.fullpath ?? '' },
    infoText: pressure?.text ?? '',
  })) ?? []

  const doubleWalledPressureStep = systemFinderListing?.doubleWalledPressure?.map(pressure => ({
    id: pressure?.doubleWalledPressureFilter?.id ?? '',
    heading: { text: pressure?.headline ?? '' },
    image: { fullpath: pressure?.image?.fullpath ?? '' },
    infoText: pressure?.text ?? '',
  })) ?? []

  const stepValues = [{
    heading: { text: `${currentStep + 1}. ${String(systemFinderListing?.projectHeadline ?? 'Bauvorhaben')}` },
    generalInfoText: systemFinderListing?.projectText ?? '',
    cardData: systemFinderListing?.project?.map(project => ({
      id: project?.projectFilter?.id ?? '',
      heading: { text: project?.headline ?? '' },
      image: { fullpath: project?.image?.fullpath ?? '' },
      infoText: project?.text ?? '',
    })) ?? [{
      id: '1',
      heading: { text: 'No Value' },
      image: { fullpath: '' },
      infoText: 'No Value was Provided',
    }],
    setValue: dispatch,
    alreadySelectedValue: firstStepValue,
  },
  {
    heading: { text: `${currentStep + 1}. ${String(systemFinderListing?.operatingModeHeadline ?? 'Betriebsart')}` },
    generalInfoText: systemFinderListing?.operatingModeText ?? '',
    cardData: systemFinderListing?.operatingMode?.map(operatingMode => ({
      id: operatingMode?.operatingModeFilter?.id ?? '',
      heading: { text: operatingMode?.headline ?? '' },
      image: { fullpath: operatingMode?.image?.fullpath ?? '' },
      infoText: operatingMode?.text ?? '',
    })) ?? [{
      id: '1',
      heading: { text: 'No Value' },
      image: { fullpath: '' },
      infoText: 'No Value was Provided',
    }],
    setValue: dispatch,
    alreadySelectedValue: secondStepValue,
  },
  {
    heading: { text: `${currentStep + 1}. ${String(systemFinderListing?.pressureHeadline ?? 'Druck')}` },
    generalInfoText: systemFinderListing?.pressureText ?? '',
    cardData: usePressureStep ? pressureStep : doubleWalledPressureStep,
    setValue: dispatch,
    alreadySelectedValue: thirdStepValue,
  },
  {
    heading: { text: `${currentStep + 1}. ${String(systemFinderListing?.executionHeadline ?? 'Ausführung')}` },
    generalInfoText: systemFinderListing?.executionText ?? '',
    cardData: systemFinderListing?.execution?.map(execution => ({
      id: execution?.executionFilter?.id ?? '',
      heading: { text: execution?.headline ?? '' },
      image: { fullpath: execution?.image?.fullpath ?? '' },
      infoText: execution?.text ?? '',
    })) ?? [{
      id: '1',
      heading: { text: 'No Value' },
      image: { fullpath: '' },
      infoText: 'No Value was Provided',
    }],
    setValue: dispatch,
    alreadySelectedValue: fourthStepValue,
  }]

  const systemsData = allPossibleSystems?.map(system => {
    const linkableSystem = linkableConfiguratorSystem(system?.node?.relatedConfigurator?.code, systems?.systems)

    // Build configurator link with UUID if found
    const baseConfiguratorUrl = process.env.NEXT_PUBLIC_CONFIGURATOR_FRONTEND_URL ?? 'https://konfigurator.stocker-kaminsysteme.com'
    const hasValidConfiguratorLink = !!linkableSystem
    const configurationLink = linkableSystem
      ? `${baseConfiguratorUrl}?systemId=${linkableSystem.id}`
      : baseConfiguratorUrl

    return {
      name: system?.node?.coloredName ?? '',
      description: system?.node?.description ?? '',
      image: { fullpath: system?.node?.infoImage?.fullpath ?? '' },
      detailLink: `/category/${String(system?.node?.slug)}/information`,
      categoryLink: `/category/${String(system?.node?.slug)}`,
      relatedProducts: (system?.node?.relatedProducts ?? [])
        .filter((p): p is NonNullable<typeof p> => p !== null && !!p?.slug)
        .map((p) => ({ slug: p.slug!, name: p.name ?? p.slug! })),
      relatedCategories: (system?.node?.relatedCategories ?? [])
        .filter((l): l is NonNullable<typeof l> => l !== null && !!l?.slug)
        .map((l) => ({ slug: String(l.slug!), name: String(l.coloredName ?? l.slug!) })),
      configurationLink,
      hasValidConfiguratorLink,
      systemValues: [
        {
          valueName: formatMessage({ id: 'system-finder--material' }),
          value: system?.node?.material ?? '',
        },
        {
          valueName: formatMessage({ id: 'system-finder--diameter' }),
          value: system?.node?.diameter ?? '',
        },
        {
          valueName: formatMessage({ id: 'system-finder--wall-thickness' }),
          value: system?.node?.wallThickness ?? '',
        },
        {
          valueName: formatMessage({ id: 'system-finder--max-exhaust-temperatur' }),
          value: system?.node?.maxExhaustTemperature ?? '',
        },
      ],
    }
  })

  if (isLoadingSystemFinderOptions) {
    return (
      <Container maxW="container.page" h="60vh">
        <Center h="100%">
          <Spinner size="xl"/>
        </Center>
      </Container>
    )
  }

  return (
    <Box>
      <Seo
        title={formatMessage({ id: 'seo--system-finder' })}
      />
      <Container maxW="container.page" py={{ base: '15px', xl: '50px' }} h="100%">
        <Breadcrumbs data={[{ label: 'Home', slug: '/' }, { label: 'System Finder', slug: '/systemfinder' }]}/>
        <SystemFinderProgressSteps
          steps={steps}
          currentStep={currentStep === stepValues.length ? stepValues.length - 1 : currentStep}
          setStep={setStep}
          showActiveAsCompleted={currentStep === stepValues.length}
          showActiveAsNotActive={!systemFinderStarted}
        />
        <Flex h="calc(100% - 150px)" align="center" justify="center">
          {!systemFinderStarted && (
            <SystemFinderStartPage
              heading={{ text: systemFinderListing?.introHeadline ?? '' }}
              image={{ fullpath: systemFinderListing?.introImage?.fullpath ?? '' }}
              description={systemFinderListing?.introText ?? ''}
              setSystemFinderStarted={setSystemFinderStarted}
            />
          )}
          {systemFinderStarted && (currentStep !== stepValues.length) && (
            <Box w="100%">
              {stepValues.map((step, index) => (
                <Box key={String(step.heading.text) + String(step.cardData[0]?.infoText ?? index)}>
                  {index === currentStep && (() => {
                      const previousStepsHaveValues = stepsValues.slice(0, currentStep).every(s => s.id !== '')
                      const hasEnoughStepEntries = stepsValues.filter(s => s.id !== '').length >= currentStep
                      const previousStepsCompleted = previousStepsHaveValues && hasEnoughStepEntries && stateInSync
                      const isDataLoaded = !!systemFinderSystemsData && systemFinderSystemsData.length > 0
                      return (
                        <SystemFinderSelectionPage
                          heading={step.heading}
                          generalInfoText={step.generalInfoText}
                          cardData={step.cardData}
                          currentStep={currentStep}
                          totalSteps={stepValues.length}
                          isDataLoaded={isDataLoaded}
                          previousStepsCompleted={previousStepsCompleted}
                          setStep={setStep}
                          setSystemFinderStarted={setSystemFinderStarted}
                          setStepValue={step.setValue}
                          alreadySelectedValue={step.alreadySelectedValue?.name ?? ''}
                          allCurrentPossibleSystem={allPossibleSystems ?? []}
                        />
                      )
                    })()}
                </Box>
              ))}
            </Box>
          )}
          {currentStep === stepValues.length && (
            <SystemFinderSystemSelectPage currentStep={currentStep} setStep={setStep} systemsData={systemsData}/>
          )}
        </Flex>
      </Container>
    </Box>
  )
}
